import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { documentsApi, clientsApi, formatApiError } from '../../services/api';
import {
  CircleNotch,
  CheckCircle,
  Warning,
  UploadSimple,
  DownloadSimple,
  Eye,
  Trash,
  ArrowsCounterClockwise,
  MagnifyingGlass,
  IdentificationCard,
  House,
  MapPin,
  Camera,
  UserFocus,
  CurrencyDollar,
  FileText,
  Gavel,
  CaretDown,
  ListChecks,
  Users,
  Car,
  X
} from '@phosphor-icons/react';

const CATEGORY_META = {
  'Identificaciones': { icon: IdentificationCard, color: '#0055FF', bg: '#EFF6FF' },
  'Domicilios': { icon: House, color: '#7C3AED', bg: '#F5F3FF' },
  'Actividad en Plataformas': { icon: UserFocus, color: '#059669', bg: '#ECFDF5' },
  'Documentos Legales': { icon: Gavel, color: '#B45309', bg: '#FFFBEB' },
  'Vehículo': { icon: Car, color: '#DC2626', bg: '#FEF2F2' },
  'Otros': { icon: FileText, color: '#64748B', bg: '#F8FAFC' },
};

const DOC_ICON = {
  ubicacion_google_maps: MapPin,
  fotografia_casa: Camera,
  foto_perfil_plataformas: Camera,
  ingresos_plataformas: CurrencyDollar,
  foto_conductor_auto: Camera,
  tarjeta_circulacion: Car,
  poliza_seguros: FileText,
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentRow = ({ row, onUpload, onDownload, onDelete, onToggleValidate, onPreview, onUpdateExpiry, onToggleNotApplicable, uploading, canValidate, canDelete }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const done = row.status === 'completado';
  const notApplicable = row.status === 'no_aplica' || row.not_applicable;
  const canBeNotApplicable = ['ine_aval_frente', 'ine_aval_reverso', 'comprobante_domicilio_aval'].includes(row.key);
  const Icon = DOC_ICON[row.key] || FileText;
  const isPoliza = row.key === 'poliza_seguros';

  const expiryInfo = useMemo(() => {
    if (!isPoliza || !row.expiry_date) return null;
    const d = new Date(row.expiry_date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);
    let cls = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    let text = `Vigente • ${days}d`;
    if (days < 0) {
      cls = 'bg-red-50 text-red-700 border border-red-200';
      text = `Vencida hace ${Math.abs(days)}d`;
    } else if (days <= 15) {
      cls = 'bg-red-50 text-red-700 border border-red-200';
      text = `Vence en ${days}d`;
    } else if (days <= 30) {
      cls = 'bg-amber-50 text-amber-700 border border-amber-200';
      text = `Vence en ${days}d`;
    }
    return { cls, text };
  }, [isPoliza, row.expiry_date]);

  const handleFile = (file) => {
    if (!file) return;
    onUpload(row.key, file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <tr className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`doc-row-${row.key}`}>
      <td className="py-3 pr-3">
        <div className="flex items-start gap-2">
          <Icon size={16} className="text-[#64748B] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-[#0F172A]">{row.label}</div>
            {row.original_name && (
              <div className="text-[10px] text-[#94A3B8] truncate max-w-[280px]" title={row.original_name}>
                {row.original_name} · {fmtSize(row.size)}
              </div>
            )}
            {notApplicable && (row.not_applicable_reason || row.not_applicable_by_name) && (
              <div
                className="text-[10px] text-slate-500 max-w-[280px] mt-0.5 italic"
                title={`Marcado por ${row.not_applicable_by_name || '—'}${row.not_applicable_at ? ' · ' + fmtDate(row.not_applicable_at) : ''}`}
                data-testid={`doc-not-applicable-reason-${row.key}`}
              >
                Motivo: {row.not_applicable_reason || '—'}
              </div>
            )}
            {isPoliza && done && row.file_id && (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="date"
                  defaultValue={row.expiry_date || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if ((v || '') !== (row.expiry_date || '')) {
                      onUpdateExpiry && onUpdateExpiry(row.file_id, v);
                    }
                  }}
                  className="text-[11px] border border-[#E2E8F0] rounded px-1.5 py-0.5"
                  title="Fecha de vigencia de la póliza"
                  data-testid={`poliza-expiry-${row.file_id}`}
                />
                {expiryInfo && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${expiryInfo.cls}`}>{expiryInfo.text}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 text-center">
        {row.mandatory ? (
          <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold">
            <CheckCircle size={14} weight="fill" />
          </span>
        ) : (
          <span className="text-xs text-[#94A3B8]">—</span>
        )}
      </td>
      <td className="py-3 pr-3">
        {notApplicable ? (
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200"
              title="Este documento fue marcado como 'No aplica'"
              data-testid={`doc-not-applicable-badge-${row.key}`}
            >
              <CheckCircle size={12} weight="fill" />
              No aplica
            </span>
            <button
              type="button"
              onClick={() => onToggleNotApplicable && onToggleNotApplicable(row, true)}
              className="text-[10px] text-[#64748B] hover:text-[#E11D48] underline"
              title="Revertir: volver a exigir este documento"
              data-testid={`doc-not-applicable-revert-${row.key}`}
            >
              Revertir
            </button>
          </div>
        ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`inline-flex items-center gap-2 transition-all ${dragOver ? 'ring-2 ring-[#0055FF] ring-offset-1' : ''}`}
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`doc-input-${row.key}`}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              done
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                : 'bg-[#EFF6FF] text-[#0055FF] border-[#BFDBFE] hover:bg-[#DBEAFE]'
            }`}
            title={done ? 'Reemplazar archivo' : 'Subir archivo'}
            data-testid={`doc-upload-btn-${row.key}`}
          >
            {uploading ? <CircleNotch size={12} className="animate-spin" /> : done ? <ArrowsCounterClockwise size={12} weight="bold" /> : <UploadSimple size={12} weight="bold" />}
            {uploading ? 'Subiendo…' : done ? 'Reemplazar' : 'Subir'}
          </button>
          {!done && canBeNotApplicable && (
            <button
              type="button"
              onClick={() => onToggleNotApplicable && onToggleNotApplicable(row, false)}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50"
              title="Marcar como 'No aplica' (por ejemplo cuando el conductor no tiene aval)"
              data-testid={`doc-not-applicable-btn-${row.key}`}
            >
              No aplica
            </button>
          )}
          {done && row.file_id && (
            <>
              <button
                type="button"
                onClick={() => onPreview && onPreview(row)}
                className="p-1.5 text-[#64748B] hover:text-[#0055FF] rounded"
                title="Vista previa"
                data-testid={`doc-view-${row.key}`}
              >
                <Eye size={14} />
              </button>
              <a
                href={documentsApi.downloadClientDoc(row.file_id)}
                download
                className="p-1.5 text-[#64748B] hover:text-[#0055FF] rounded"
                title="Descargar"
                data-testid={`doc-download-${row.key}`}
              >
                <DownloadSimple size={14} />
              </a>
              {canDelete && (
                <button
                  onClick={() => onDelete(row.file_id)}
                  className="p-1.5 text-[#E11D48] hover:bg-red-50 rounded"
                  title="Eliminar"
                  data-testid={`doc-delete-${row.key}`}
                >
                  <Trash size={13} />
                </button>
              )}
            </>
          )}
        </div>
        )}
      </td>
      <td className="py-3">
        {notApplicable ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200">
            <CheckCircle size={12} weight="fill" /> No aplica
          </span>
        ) : done ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle size={12} weight="fill" /> Completado
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Warning size={12} weight="fill" /> Pendiente
          </span>
        )}
      </td>
      <td className="py-3 text-center">
        <label className={`inline-flex items-center justify-center ${(canValidate && done) || notApplicable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
          <input
            type="checkbox"
            checked={!!row.validated_by_analyst || notApplicable}
            disabled={notApplicable || !canValidate || !done}
            onChange={(e) => onToggleValidate(row.file_id, e.target.checked)}
            className="w-4 h-4 accent-[#059669]"
            data-testid={`doc-validate-${row.key}`}
          />
        </label>
        {row.validated_by && (
          <div className="text-[10px] text-[#94A3B8] mt-0.5" title={fmtDate(row.validated_at)}>
            {row.validated_by.split('@')[0]}
          </div>
        )}
      </td>
    </tr>
  );
};

const ClientSearchSelect = ({ clients, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const selected = clients.find((c) => c.id === value);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const t = `${c.name || ''} ${c.phone || ''} ${c.email || ''}`.toLowerCase();
      return t.includes(q);
    });
  }, [clients, query]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="input w-full text-left flex items-center justify-between"
        data-testid="expediente-client-select"
      >
        <span className={selected ? 'text-[#0F172A]' : 'text-[#94A3B8]'}>
          {selected ? `${selected.name}${selected.phone ? ` · ${selected.phone}` : ''}` : '— Selecciona un cliente —'}
        </span>
        <CaretDown size={14} className="text-[#64748B]" />
      </button>

      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-xl max-h-80 overflow-hidden">
          <div className="p-2 border-b border-[#E2E8F0] flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-[#94A3B8]" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, teléfono o correo…"
              className="flex-1 text-sm outline-none"
              data-testid="expediente-client-search"
            />
            <span className="text-[10px] text-[#94A3B8]">{filtered.length}</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-[#94A3B8]">Sin coincidencias</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F1F5F9] flex items-center justify-between ${c.id === value ? 'bg-[#EFF6FF] text-[#0055FF] font-medium' : 'text-[#0F172A]'}`}
                  data-testid={`expediente-client-option-${c.id}`}
                >
                  <div>
                    <div>{c.name}</div>
                    {(c.phone || c.email) && <div className="text-[10px] text-[#94A3B8]">{c.phone || ''}{c.phone && c.email ? ' · ' : ''}{c.email || ''}</div>}
                  </div>
                  {c.id === value && <CheckCircle size={14} weight="fill" className="text-[#0055FF]" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ExpedienteTab = ({ canValidate = false, canDelete = false }) => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [summary, setSummary] = useState({ completed: 0, total_required: 19, validated: 0 });
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'consolidated'
  const [consolidated, setConsolidated] = useState(null);
  const [loadingConsolidated, setLoadingConsolidated] = useState(false);
  const [consolidatedQuery, setConsolidatedQuery] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const loadClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      const { data } = await clientsApi.getAll();
      setClients(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const loadChecklist = useCallback(async (cid) => {
    if (!cid) return;
    try {
      setLoading(true);
      const { data } = await documentsApi.getClientChecklist(cid);
      setChecklist(data.checklist);
      setSummary({
        completed: data.completed,
        total_required: data.total_required,
        validated: data.validated,
      });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { if (selectedClientId) loadChecklist(selectedClientId); }, [selectedClientId, loadChecklist]);

  const loadConsolidated = useCallback(async () => {
    try {
      setLoadingConsolidated(true);
      const { data } = await documentsApi.getClientsChecklistSummary();
      setConsolidated(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoadingConsolidated(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'consolidated' && !consolidated) loadConsolidated();
  }, [viewMode, consolidated, loadConsolidated]);

  const handleUpload = async (docKey, file) => {
    if (!selectedClientId) return;
    try {
      setUploadingKey(docKey);
      setError('');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_id', selectedClientId);
      fd.append('doc_type', docKey);
      // Al subir la Póliza, pedir la fecha de vigencia opcional
      if (docKey === 'poliza_seguros') {
        const dateStr = window.prompt('Fecha de vigencia de la póliza (YYYY-MM-DD). Deja vacío si no la conoces ahora — podrás capturarla después:', '');
        if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
          fd.append('expiry_date', dateStr.trim());
        }
      }
      await documentsApi.uploadClientDoc(fd);
      await loadChecklist(selectedClientId);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploadingKey(null);
    }
  };

  const handleUpdateExpiry = async (fileId, expiryDate) => {
    try {
      await documentsApi.updateClientDocMeta(fileId, { expiry_date: expiryDate });
      await loadChecklist(selectedClientId);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleToggleNotApplicable = async (row, currentlyNotApplicable) => {
    if (!selectedClientId) return;
    if (currentlyNotApplicable) {
      if (!window.confirm(`¿Revertir el "No aplica" y volver a exigir "${row.label}"?`)) return;
      try {
        await documentsApi.markDocNotApplicable({
          client_id: selectedClientId,
          doc_type: row.key,
          applicable: true,
        });
        await loadChecklist(selectedClientId);
      } catch (err) {
        setError(formatApiError(err));
      }
      return;
    }
    const reason = window.prompt(
      `Marcar "${row.label}" como "No aplica".\n\nMotivo (visible en auditoría). Ejemplo: "El conductor no tiene aval":`,
      'El conductor no tiene aval',
    );
    if (reason === null) return; // Cancel
    try {
      await documentsApi.markDocNotApplicable({
        client_id: selectedClientId,
        doc_type: row.key,
        applicable: false,
        reason: (reason || '').trim(),
      });
      await loadChecklist(selectedClientId);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('¿Eliminar este documento? Esta acción no se puede deshacer.')) return;
    try {
      await documentsApi.deleteClientDoc(fileId);
      await loadChecklist(selectedClientId);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleValidate = async (fileId, validated) => {
    if (!fileId) return;
    try {
      await documentsApi.toggleValidateClientDoc(fileId, validated);
      await loadChecklist(selectedClientId);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const grouped = useMemo(() => {
    const byCat = {};
    checklist.forEach((r) => {
      const cat = r.category || 'Otros';
      if (!byCat[cat]) byCat[cat] = [];
      byCat[cat].push(r);
    });
    return byCat;
  }, [checklist]);

  const orderedCats = ['Identificaciones', 'Domicilios', 'Actividad en Plataformas', 'Documentos Legales', 'Vehículo', 'Otros'];
  const pct = summary.total_required ? Math.round((summary.completed / summary.total_required) * 100) : 0;
  const selectedClient = clients.find((c) => c.id === selectedClientId);

  return (
    <div data-testid="expediente-tab">
      {/* View mode toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setViewMode('single')}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            viewMode === 'single'
              ? 'bg-[#0055FF] text-white border-[#0055FF]'
              : 'bg-white text-[#64748B] border-[#E2E8F0] hover:text-[#0F172A]'
          }`}
          data-testid="view-mode-single"
        >
          <ListChecks size={14} /> Matriz por conductor
        </button>
        <button
          onClick={() => { setViewMode('consolidated'); }}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            viewMode === 'consolidated'
              ? 'bg-[#0055FF] text-white border-[#0055FF]'
              : 'bg-white text-[#64748B] border-[#E2E8F0] hover:text-[#0F172A]'
          }`}
          data-testid="view-mode-consolidated"
        >
          <Users size={14} /> Vista Consolidada
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

      {viewMode === 'consolidated' ? (
        <ConsolidatedView
          data={consolidated}
          loading={loadingConsolidated}
          query={consolidatedQuery}
          onQueryChange={setConsolidatedQuery}
          onSelectClient={(cid) => { setSelectedClientId(cid); setViewMode('single'); }}
          onRefresh={loadConsolidated}
        />
      ) : (
        <>
      {/* Client selector + summary */}
      <div className="card p-4 mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-center">
          <div className="lg:col-span-2">
            <label className="label mb-1">Selecciona Conductor / Acreditado *</label>
            <ClientSearchSelect
              clients={clients}
              value={selectedClientId}
              onChange={setSelectedClientId}
              disabled={loadingClients}
            />
          </div>

          <div className="col-span-1">
            <div className="text-xs text-[#64748B]">Completados</div>
            <div className="text-2xl font-heading font-bold text-[#0F172A]">
              {summary.completed}<span className="text-sm text-[#94A3B8] font-normal">/{summary.total_required}</span>
            </div>
            <div className="w-full bg-[#F1F5F9] rounded-full h-1.5 mt-1">
              <div
                className={`h-1.5 rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-[#0055FF]' : 'bg-amber-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          <div className="col-span-1">
            <div className="text-xs text-[#64748B]">Validados por analista</div>
            <div className="text-2xl font-heading font-bold text-emerald-600">
              {summary.validated}<span className="text-sm text-[#94A3B8] font-normal">/{summary.total_required}</span>
            </div>
            {selectedClient && (
              <div className="text-[10px] text-[#94A3B8] mt-1 truncate">Cliente: {selectedClient.name}</div>
            )}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {!selectedClientId ? (
        <div className="card p-12 text-center">
          <FileText size={40} className="mx-auto text-[#CBD5E1] mb-3" />
          <div className="text-[#0F172A] font-semibold mb-1">Selecciona un conductor</div>
          <div className="text-sm text-[#64748B]">Elige un cliente arriba para ver su matriz de expediente.</div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      ) : (
        <div className="space-y-4">
          {orderedCats.map((cat) => {
            const rows = grouped[cat];
            if (!rows || rows.length === 0) return null;
            const meta = CATEGORY_META[cat] || CATEGORY_META['Otros'];
            const CatIcon = meta.icon;
            const doneInCat = rows.filter((r) => r.status === 'completado').length;
            return (
              <div key={cat} className="card p-4" data-testid={`expediente-cat-${cat}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: meta.bg, color: meta.color }}
                  >
                    <CatIcon size={18} weight="duotone" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-heading font-bold text-[#0F172A]">{cat}</h3>
                    <div className="text-xs text-[#64748B]">
                      {doneInCat} de {rows.length} completados
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B] border-b border-[#E2E8F0]">
                        <th className="py-2 pr-3 font-semibold">Documento</th>
                        <th className="py-2 text-center font-semibold w-24">Obligatorio</th>
                        <th className="py-2 pr-3 font-semibold w-64">Archivo</th>
                        <th className="py-2 font-semibold w-32">Estatus</th>
                        <th className="py-2 text-center font-semibold w-32">Validado por Analista</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <DocumentRow
                          key={r.key + (r.file_id || '')}
                          row={r}
                          uploading={uploadingKey === r.key}
                          onUpload={handleUpload}
                          onDelete={handleDelete}
                          onToggleValidate={handleValidate}
                          onPreview={setPreviewDoc}
                          onUpdateExpiry={handleUpdateExpiry}
                          onToggleNotApplicable={handleToggleNotApplicable}
                          canValidate={canValidate}
                          canDelete={canDelete}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}

      {/* Modal de vista previa del documento */}
      {previewDoc && previewDoc.file_id && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewDoc(null)}
          data-testid="preview-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="preview-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <div className="flex items-center gap-2 min-w-0">
                <Eye size={18} className="text-[#0055FF] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0F172A] truncate">{previewDoc.label}</div>
                  <div className="text-[11px] text-[#64748B] truncate">{previewDoc.original_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={documentsApi.downloadClientDoc(previewDoc.file_id)}
                  download
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#EFF6FF] text-[#0055FF] border border-[#BFDBFE] hover:bg-[#DBEAFE]"
                  data-testid="preview-download-btn"
                >
                  <DownloadSimple size={13} weight="bold" /> Descargar
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="p-2 text-[#64748B] hover:text-[#E11D48] hover:bg-red-50 rounded-lg"
                  title="Cerrar"
                  data-testid="preview-close-btn"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-[#0F172A]/95">
              <iframe
                key={previewDoc.file_id}
                src={documentsApi.previewClientDoc(previewDoc.file_id)}
                title={previewDoc.label}
                className="w-full h-full border-0 bg-white"
                data-testid="preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const ConsolidatedView = ({ data, loading, query, onQueryChange, onSelectClient, onRefresh }) => {
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = (query || '').trim().toLowerCase();
    if (!q) return data.clients;
    return data.clients.filter((c) => `${c.name} ${c.phone}`.toLowerCase().includes(q));
  }, [data, query]);

  if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const barColor = (pct) => (pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-[#0055FF]' : pct > 0 ? 'bg-amber-500' : 'bg-[#CBD5E1]');

  return (
    <div data-testid="consolidated-view">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="card p-3">
          <div className="text-xs text-[#64748B]">Total conductores</div>
          <div className="text-2xl font-heading font-bold text-[#0F172A]">{data.total_clients}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-[#64748B]">Expediente completo</div>
          <div className="text-2xl font-heading font-bold text-emerald-600">{data.fully_completed}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-[#64748B]">Expediente validado</div>
          <div className="text-2xl font-heading font-bold text-[#059669]">{data.fully_validated}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-[#64748B]">Docs requeridos</div>
          <div className="text-2xl font-heading font-bold text-[#0055FF]">{data.total_required}</div>
        </div>
      </div>

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="text-sm font-heading font-bold text-[#0F172A]">Todos los Conductores</h3>
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-2 flex-1">
              <MagnifyingGlass size={14} className="text-[#94A3B8]" />
              <input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Buscar conductor…"
                className="flex-1 py-1.5 text-sm outline-none"
                data-testid="consolidated-search"
              />
            </div>
            <a
              href={documentsApi.checklistSummaryExcelUrl()}
              download
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              title="Descargar tabla completa a Excel"
              data-testid="export-consolidated-excel"
            >
              <DownloadSimple size={13} weight="bold" /> Excel
            </a>
            <button
              onClick={onRefresh}
              className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-[#64748B] hover:text-[#0F172A]"
              title="Recargar"
            >
              <ArrowsCounterClockwise size={14} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B] border-b border-[#E2E8F0]">
                <th className="py-2 pr-3 font-semibold">Conductor</th>
                <th className="py-2 pr-3 font-semibold">Teléfono</th>
                <th className="py-2 pr-3 font-semibold">Completados</th>
                <th className="py-2 pr-3 font-semibold">Validados</th>
                <th className="py-2 pr-3 font-semibold w-48">Progreso</th>
                <th className="py-2 text-right font-semibold w-24">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="py-6 text-center text-sm text-[#94A3B8]">Sin resultados</td></tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.client_id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`consolidated-row-${c.client_id}`}>
                    <td className="py-2.5 pr-3 text-sm font-medium text-[#0F172A]">{c.name}</td>
                    <td className="py-2.5 pr-3 text-xs text-[#64748B]">{c.phone || '—'}</td>
                    <td className="py-2.5 pr-3 text-sm">
                      <span className={c.completed === c.total_required ? 'text-emerald-600 font-semibold' : c.completed > 0 ? 'text-[#0055FF] font-semibold' : 'text-[#94A3B8]'}>
                        {c.completed}/{c.total_required}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-sm">
                      <span className={c.validated === c.total_required ? 'text-emerald-600 font-semibold' : c.validated > 0 ? 'text-[#059669] font-semibold' : 'text-[#94A3B8]'}>
                        {c.validated}/{c.total_required}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#F1F5F9] rounded-full h-2">
                          <div className={`h-2 rounded-full ${barColor(c.pct_completed)}`} style={{ width: `${c.pct_completed}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-[#0F172A] w-10 text-right">{c.pct_completed}%</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-right">
                      <button
                        onClick={() => onSelectClient(c.client_id)}
                        className="text-xs px-2 py-1 rounded-lg border border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF] font-semibold"
                        data-testid={`open-expediente-${c.client_id}`}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpedienteTab;
